import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  updateDoc,
  doc,
  QueryConstraint,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase/firebase'; // adjust path
import { OrderModel } from '../model/orderModel';

type ScheduleFilter = 'DAILY' | 'WEEKLY' | 'MONTHLY';

interface FilterParams {
  status?: string | null;
  restaurantId?: string | null;
  deliveryPartnerId?: string | null;
  scheduleFilter: ScheduleFilter;
  selectedDate: Date;
}


export async function getDeliveredOrderCountByUser(userId: string): Promise<number> {
  // Reference to Orders collection
  const ordersRef = collection(db, 'zapee_restaurant_orders');

  // Query for orders with status 'DELIVERED' and matching deliveryPartnerId
  const deliveredQuery = query(
    ordersRef,
    where('status', '==', 'DELIVERED'),
    where('customerId', '==', userId)
  );

  // Execute query and get snapshot
  const snapshot = await getDocs(deliveredQuery);

  // Return count of matched documents (orders)
  return snapshot.size;
}

function calculateDateRange(selectedDate: Date, schedule: ScheduleFilter): { start: Date; end: Date } {
  const start = new Date(selectedDate);
  start.setHours(0, 0, 0, 0);

  let end: Date;

  switch (schedule) {
    case 'DAILY':
      end = new Date(start);
      end.setDate(end.getDate() + 1);
      break;
    case 'WEEKLY':
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      end = new Date(start);
      end.setDate(end.getDate() + 7);
      break;
    case 'MONTHLY':
      start.setDate(1);
      end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      break;
    default:
      end = new Date(start);
      end.setDate(end.getDate() + 1);
  }

  return { start, end };
}

export async function updateOrder(orderId: string, updateFields: Partial<OrderModel>): Promise<void> {
  try {
    const orderRef = doc(db, 'zapee_restaurant_orders', orderId);
    await updateDoc(orderRef, updateFields);
    // Optionally return a success message or resolve
  } catch (error: any) {
    // Handle error, log or show user message as needed
    console.error('Failed to update order:', error);
    throw error;
  }
}

export const usePendingOrders = () => {
  const [filterParams, setFilterParams] = useState<FilterParams>({
    status: null,
    restaurantId: null,
    deliveryPartnerId: null,
    scheduleFilter: 'DAILY',
    selectedDate: new Date(),
  });

  const [orders, setOrders] = useState<OrderModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Totals including packing
  const [totalDeliveryCharge, setTotalDeliveryCharge] = useState(0);
  const [totalPacking, setTotalPacking] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [totalService, setTotalService] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  // Delivered-only totals
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [deliveredTotalPrice, setDeliveredTotalPrice] = useState(0);
  const [deliveredDeliveryCharge, setDeliveredDeliveryCharge] = useState(0);
  const [deliveredPacking, setDeliveredPacking] = useState(0);
  const [deliveredTax, setDeliveredTax] = useState(0);
  const [deliveredService, setDeliveredService] = useState(0);

  useEffect(() => {
    if (!auth.currentUser) {
      setOrders([]);
      setEmpty(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    const { start, end } = calculateDateRange(filterParams.selectedDate, filterParams.scheduleFilter);

    // Admin fetches all orders — access controlled by isAdmin() in Firestore rules
    const constraints: QueryConstraint[] = [
      where('createdAt', '>=', Timestamp.fromDate(start)),
      where('createdAt', '<', Timestamp.fromDate(end)),
      orderBy('createdAt', 'desc'),
    ];

    if (filterParams.status && filterParams.status !== '') {
      constraints.push(where('status', '==', filterParams.status));
    }
    if (filterParams.restaurantId && filterParams.restaurantId !== '') {
      constraints.push(where('orderNumber', 'array-contains', filterParams.restaurantId));
    }
    if (filterParams.deliveryPartnerId && filterParams.deliveryPartnerId !== '') {
      constraints.push(where('deliveryPartnerId', '==', filterParams.deliveryPartnerId));
    }

    const q = query(collection(db, 'zapee_restaurant_orders'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedOrders: OrderModel[] = snapshot.docs.map(d => {
          const data = d.data() as OrderModel;
          if (data.createdAt && (data.createdAt as any).toDate) {
            data.createdAt = (data.createdAt as any).toDate();
          }
          return { id: d.id, ...data };
        });

        setOrders(fetchedOrders);
        setEmpty(fetchedOrders.length === 0);

        let deliveryChargeSum = 0;
        let packingSum = 0;
        let taxSum = 0;
        let serviceSum = 0;
        let priceSum = 0;

        let dCount = 0;
        let dPrice = 0;
        let dDelivery = 0;
        let dPacking = 0;
        let dTax = 0;
        let dService = 0;

        fetchedOrders.forEach(order => {
          if (order.charges) {
            deliveryChargeSum += order.charges.deliveryCharge || 0;
            packingSum += order.charges.packing || 0;
            taxSum += order.charges.tax || 0;
            serviceSum += order.charges.service || 0;
          }
          priceSum += order.totalPrice || 0;

          if ((order.status || '').toUpperCase() === 'DELIVERED') {
            dCount++;
            dPrice += order.totalPrice || 0;
            if (order.charges) {
              dDelivery += order.charges.deliveryCharge || 0;
              dPacking += order.charges.packing || 0;
              dTax += order.charges.tax || 0;
              dService += order.charges.service || 0;
            }
          }
        });

        setTotalDeliveryCharge(deliveryChargeSum);
        setTotalPacking(packingSum);
        setTotalTax(taxSum);
        setTotalService(serviceSum);
        setTotalPrice(priceSum);

        setDeliveredCount(dCount);
        setDeliveredTotalPrice(dPrice);
        setDeliveredDeliveryCharge(dDelivery);
        setDeliveredPacking(dPacking);
        setDeliveredTax(dTax);
        setDeliveredService(dService);
        setLoading(false);
      },
      (error) => {
        setOrders([]);
        setEmpty(true);
        setErrorMessage(`Failed to fetch orders: ${error.message}`);
        setTotalDeliveryCharge(0);
        setTotalPacking(0);
        setTotalTax(0);
        setTotalService(0);
        setTotalPrice(0);
        setDeliveredCount(0);
        setDeliveredTotalPrice(0);
        setDeliveredDeliveryCharge(0);
        setDeliveredPacking(0);
        setDeliveredTax(0);
        setDeliveredService(0);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [filterParams]);

  return {
    orders,
    loading,
    empty,
    errorMessage,
    totalDeliveryCharge,
    totalPacking,
    totalTax,
    totalService,
    totalPrice,
    deliveredCount,
    deliveredTotalPrice,
    deliveredDeliveryCharge,
    deliveredPacking,
    deliveredTax,
    deliveredService,
    setFilters: (
      status: string | null,
      restaurantId: string | null,
      deliveryPartnerId: string | null,
      scheduleFilter: ScheduleFilter,
      selectedDate: Date
    ) => {
      setFilterParams({ status, restaurantId, deliveryPartnerId, scheduleFilter, selectedDate });
    },
  };


};
